import { Lightning } from '@lightningjs/sdk'
import { getColor, grey300, grey50 } from '../../constants/colors'
import { UniNeueBold, UniNeueRegular } from '../../constants/fonts'
import { AppConfig } from '../../services/appConfig'
import { SportData } from '../../types/flo-experience'
import { joinStrings } from '../../utils/misc'
import {
  DETAILS_FONT_SIZE,
  HIDDEN,
  INNER_MARGIN,
  INNER_PADDING,
  TITLE_FONT_SIZE,
} from './templates'

export const renderScore = (score) => {
  const scoresEnabled = AppConfig.shared.appSettings.areLiveScoresEnabled()

  return scoresEnabled ? score : '**'
}

export const getSportsDataLayout = (data: SportData, cardWidth: number) => {
  if (data?.sportType === 'versus') {
    return {
      type: VersusVariation,
      cardWidth: cardWidth,
      data: data,
    }
  }

  if (data?.sportType === 'routine') {
    return {
      type: RoutineVariation,
      cardWidth: cardWidth,
      data: data,
    }
  }

  if (data?.sportType === 'event') {
    return {
      type: EventVariation,
      cardWidth: cardWidth,
      data: data,
    }
  }
}

export class VersusVariation extends Lightning.Component {
  private _data: SportData
  private _cardWidth: number

  static _template() {
    return {
      w: (w) => w,
      h: (h) => h,
      flex: { direction: 'column', padding: INNER_PADDING },
      FirstRow: {
        w: (w) => w,
        flex: {
          direction: 'row',
          justifyContent: 'space-between',
        },
        flexItem: { marginBottom: INNER_MARGIN / 2 },
        Other: {
          text: {
            text: '',
            textColor: getColor(grey300),
            fontFace: UniNeueRegular,
            fontSize: DETAILS_FONT_SIZE,
          },
        },
        TimePeriod: {
          x: -INNER_PADDING * 2,
          text: {
            text: '',
            textColor: getColor(grey300),
            fontFace: UniNeueRegular,
            fontSize: DETAILS_FONT_SIZE,
          },
        },
      },
      SecondRow: {
        w: (w) => w,
        alpha: HIDDEN,
        flex: {
          direction: 'row',
          justifyContent: 'space-between',
        },
        flexItem: { marginBottom: INNER_MARGIN / 2 },
        TitleOne: {
          text: {
            text: '',
            textColor: getColor(grey50),
            fontFace: UniNeueBold,
            fontSize: TITLE_FONT_SIZE,
            wordWrap: false,
            textOverflow: '...',
          },
        },
        ScoreOne: {
          x: -INNER_PADDING * 2,
          text: {
            text: '',
            textColor: getColor(grey50),
            fontFace: UniNeueBold,
            fontSize: TITLE_FONT_SIZE,
          },
        },
      },
      ThirdRow: {
        w: (w) => w,
        alpha: HIDDEN,
        flex: {
          direction: 'row',
          justifyContent: 'space-between',
        },
        TitleTwo: {
          text: {
            text: '',
            textColor: getColor(grey50),
            fontFace: UniNeueBold,
            fontSize: TITLE_FONT_SIZE,
            wordWrap: false,
            textOverflow: '...',
          },
        },
        ScoreTwo: {
          x: -INNER_PADDING * 2,
          text: {
            text: '',
            textColor: getColor(grey50),
            fontFace: UniNeueBold,
            fontSize: TITLE_FONT_SIZE,
          },
        },
      },
    }
  }

  get secondRow() {
    return this.tag('SecondRow')
  }

  get thirdRow() {
    return this.tag('ThirdRow')
  }

  get other() {
    return this.tag('Other')
  }

  get timePeriod() {
    return this.tag('TimePeriod')
  }

  get titleOne() {
    return this.tag('TitleOne')
  }

  get scoreOne() {
    return this.tag('ScoreOne')
  }

  get titleTwo() {
    return this.tag('TitleTwo')
  }

  get scoreTwo() {
    return this.tag('ScoreTwo')
  }

  set cardWidth(v: number) {
    this._cardWidth = v
  }

  get cardWidth() {
    return this._cardWidth
  }

  set data(v: SportData) {
    this._data = v
  }

  get data() {
    return this._data
  }

  _setup() {
    this.scoreOne.on('txLoaded', () => {
      this.titleOne.patch({
        text: { wordWrapWidth: this.cardWidth - (this.scoreOne.renderWidth + INNER_PADDING * 2) },
      })
      this.secondRow.alpha = 1
    })

    this.scoreTwo.on('txLoaded', () => {
      this.titleTwo.patch({
        text: { wordWrapWidth: this.cardWidth - (this.scoreTwo.renderWidth + INNER_PADDING * 2) },
      })
      this.thirdRow.alpha = 1
    })
  }

  _init() {
    this.renderData(this.data)
  }

  renderData(data: SportData) {
    this.other.patch({ text: { text: data?.other ?? '' } })
    this.timePeriod.patch({
      text: { text: joinStrings('•', data?.period, data?.time) ?? '' },
    })
    this.titleOne.patch({
      text: {
        text: joinStrings('•', data?.title1, data?.subtitle1) ?? '',
      },
    })
    this.scoreOne.patch({ text: { text: renderScore(data?.score1 ?? '0') } })

    this.titleTwo.patch({
      text: {
        text: joinStrings('•', data?.title2, data?.subtitle2) ?? '',
      },
    })
    this.scoreTwo.patch({ text: { text: renderScore(data?.score2 ?? '0') } })
  }
}

export class RoutineVariation extends Lightning.Component {
  private _data: SportData
  private _cardWidth: number

  static _template() {
    return {
      w: (w) => w,
      h: (h) => h,
      flex: { direction: 'column', padding: INNER_PADDING },
      FirstRow: {
        w: (w) => w - INNER_PADDING * 2,
        flex: { direction: 'row' },
        flexItem: { marginBottom: INNER_MARGIN / 2 },
        PeriodOther: {
          text: {
            text: '',
            textColor: getColor(grey300),
            fontFace: UniNeueRegular,
            fontSize: DETAILS_FONT_SIZE,
          },
        },
      },
      SecondRow: {
        w: (w) => w - INNER_PADDING * 2,
        flex: { direction: 'row' },
        TitleOne: {
          text: {
            text: '',
            textColor: getColor(grey50),
            fontFace: UniNeueBold,
            fontSize: TITLE_FONT_SIZE,
            maxLines: 2,
            maxLinesSuffix: '...',
          },
        },
      },
      ThirdRow: {
        w: (w) => w - INNER_PADDING * 2,
        flex: { direction: 'row' },
        SubtitleOne: {
          text: {
            text: '',
            textColor: getColor(grey50),
            fontFace: UniNeueRegular,
            fontSize: TITLE_FONT_SIZE,
            maxLines: 1,
            maxLinesSuffix: '...',
          },
        },
      },
    }
  }

  get periodOther() {
    return this.tag('PeriodOther')
  }

  get titleOne() {
    return this.tag('TitleOne')
  }

  get subtitleOne() {
    return this.tag('SubtitleOne')
  }

  set cardWidth(v: number) {
    this._cardWidth = v
  }

  get cardWidth() {
    return this._cardWidth
  }

  set data(v: SportData) {
    this._data = v
  }

  get data() {
    return this._data
  }

  _init() {
    this.renderData(this.data)
  }

  renderData(data: SportData) {
    this.periodOther.patch({ text: { text: joinStrings('•', data?.period, data?.other) ?? '' } })
    this.titleOne.patch({
      text: { text: data?.title1 ?? '', wordWrapWidth: this.cardWidth - INNER_PADDING * 2 },
    })
    this.subtitleOne.patch({
      text: { text: data?.subtitle1 ?? '', wordWrapWidth: this.cardWidth - INNER_PADDING * 2 },
    })
  }
}

export class EventVariation extends Lightning.Component {
  private _data: SportData
  private _cardWidth: number

  static _template() {
    return {
      w: (w) => w,
      h: (h) => h,
      flex: { direction: 'column', padding: INNER_PADDING },
      FirstRow: {
        w: (w) => w - INNER_PADDING * 2,
        flex: { direction: 'row' },
        flexItem: { marginBottom: INNER_MARGIN / 2 },
        SubtitleOne: {
          text: {
            text: '',
            textColor: getColor(grey300),
            fontFace: UniNeueRegular,
            fontSize: DETAILS_FONT_SIZE,
            maxLines: 1,
            maxLinesSuffix: '...',
          },
        },
      },
      SecondRow: {
        w: (w) => w - INNER_PADDING * 2,
        flex: { direction: 'row' },
        TitleOne: {
          text: {
            text: '',
            textColor: getColor(grey50),
            fontFace: UniNeueBold,
            fontSize: TITLE_FONT_SIZE,
            maxLines: 3,
            maxLinesSuffix: '...',
          },
        },
      },
    }
  }

  get subtitleOne() {
    return this.tag('SubtitleOne')
  }

  get titleOne() {
    return this.tag('TitleOne')
  }

  set cardWidth(v: number) {
    this._cardWidth = v
  }

  get cardWidth() {
    return this._cardWidth
  }

  set data(v: SportData) {
    this._data = v
  }

  get data() {
    return this._data
  }

  _init() {
    this.renderData(this.data)
  }

  renderData(data: SportData) {
    this.titleOne.patch({
      text: { text: data?.title1 ?? '', wordWrapWidth: this.cardWidth - INNER_PADDING * 2 },
    })
    this.subtitleOne.patch({
      text: {
        text: joinStrings('•', data?.period, data?.other) ?? '',
        wordWrapWidth: this.cardWidth - INNER_PADDING * 2,
      },
    })
  }
}
